const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'

const requiredElement = <T extends HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector)
  if (!element) throw new Error(`Missing required achievement UI element: ${selector}`)
  return element
}

export type AchievementIcon = 'key'

export interface AchievementUnlock {
  id: string
  description: string
  icon: AchievementIcon
}

const createSvgElement = <K extends keyof SVGElementTagNameMap>(
  name: K,
): SVGElementTagNameMap[K] => document.createElementNS(SVG_NAMESPACE, name)

const createKeyIcon = (): SVGSVGElement => {
  const svg = createSvgElement('svg')
  svg.setAttribute('viewBox', '0 0 24 24')
  svg.setAttribute('aria-hidden', 'true')
  svg.setAttribute('focusable', 'false')

  const ring = createSvgElement('circle')
  ring.setAttribute('cx', '7.5')
  ring.setAttribute('cy', '7.5')
  ring.setAttribute('r', '4.25')

  const shaft = createSvgElement('path')
  shaft.setAttribute('d', 'm10.55 10.55 10.2 10.2m-5.6-4.6 2.2-2.2m.75 5.75 2.15-2.15')

  svg.append(ring, shaft)
  return svg
}

export class AchievementTrail {
  private readonly panel = requiredElement<HTMLElement>('#achievement-trail')
  private readonly list = requiredElement<HTMLOListElement>('#achievement-trail-list')
  private readonly unlocked = new Set<string>()

  public unlock(achievement: AchievementUnlock): boolean {
    if (this.unlocked.has(achievement.id)) return false
    this.unlocked.add(achievement.id)
    const achievementNumber = this.unlocked.size
    const hadAchievements = this.list.childElementCount > 0
    const previousWidth = this.list.getBoundingClientRect().width

    const item = document.createElement('li')
    item.className = 'achievement-trail-item'
    item.dataset.achievementId = achievement.id
    item.dataset.unlocked = 'false'

    const node = document.createElement('button')
    node.type = 'button'
    node.className = 'achievement-trail-node'
    node.setAttribute(
      'aria-label',
      `Achievement ${achievementNumber}: ${achievement.description.trim()}`,
    )
    if (achievement.icon === 'key') node.append(createKeyIcon())

    const tooltip = document.createElement('span')
    tooltip.className = 'achievement-trail-tooltip'
    tooltip.setAttribute('role', 'tooltip')

    const tooltipTitle = document.createElement('strong')
    tooltipTitle.textContent = `Achievement ${achievementNumber}:`
    const tooltipDescription = document.createElement('span')
    tooltipDescription.textContent = achievement.description.trim()
    tooltip.append(tooltipTitle, tooltipDescription)

    item.append(node, tooltip)
    this.list.append(item)
    this.panel.dataset.visible = 'true'
    this.panel.setAttribute('aria-hidden', 'false')

    if (hadAchievements) {
      const addedWidth = this.list.getBoundingClientRect().width - previousWidth
      this.list.animate(
        [
          { transform: `translateX(${addedWidth}px)` },
          { transform: 'translateX(0)' },
        ],
        {
          duration: 440,
          easing: 'cubic-bezier(0.2, 0.88, 0.28, 1.08)',
        },
      )
    }
    window.requestAnimationFrame(() => {
      item.dataset.unlocked = 'true'
    })
    return true
  }

  public dispose(): void {
    for (const animation of this.list.getAnimations()) animation.cancel()
    this.unlocked.clear()
    this.list.replaceChildren()
    this.panel.dataset.visible = 'false'
    this.panel.setAttribute('aria-hidden', 'true')
  }
}
